import React, { useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  openSupportEmail,
  createSupportPattern,
  SUPPORT_TEXT,
  SUPPORT_EMAIL,
} from '../utils/supportEmail';
import { isDevelopment } from '../env/config';
import formStyles from '../styles/SmartSendForm.module.css';

/**
 * ErrorMessageWithSupport - Component that renders error messages with clickable "Contact Support" links
 *
 * Similar to the Banishment component pattern, this component:
 * - Detects "Contact Support" or support email mentions in error messages
 * - Replaces them with clickable links that open mailto with order/error info
 * - Handles both plain text and JSX rendering
 *
 * @param {string} message - Error message that may contain "Contact Support" text
 * @param {Object} errorData - Error and order information for email
 * @param {string} errorData.orderId - Order ID
 * @param {Object} errorData.orderData - Order data object
 * @param {string} errorData.errorMessage - Error message from backend
 * @param {string} errorData.errorCode - Error code from backend
 * @param {string} errorData.phoneNumber - User's phone number
 * @param {Object} errorData.address - Address object
 * @param {string} errorData.context - Additional context about where the error occurred
 * @param {string} className - CSS class name for styling
 * @returns {React.ReactNode} Rendered error message with clickable support link
 */
const ErrorMessageWithSupport = ({
  message,
  errorData = {},
  className = '',
}) => {
  // Memoize regex patterns - useMemo ensures patterns are created only once
  // Must be called before any early returns (React Hook rules)
  //
  // ⚠️ MAINTENANCE WARNING: Empty dependency array assumes createSupportPattern() only uses
  // module-level constants (SUPPORT_TEXT and SUPPORT_EMAIL). If createSupportPattern() is
  // modified to accept parameters or use props/state/context, this dependency array MUST be
  // updated to include those dependencies, otherwise stale patterns will be used.
  //
  // Note: createSupportPattern() is lightweight (just creates 2 RegExp objects), but we keep
  // useMemo for consistency and to avoid recreating patterns on every render.

  /**
   * Handle click on support link
   */
  const handleSupportClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Fire-and-forget: openSupportEmail is async but we don't need to wait for it
    // openSupportEmail has internal fallbacks and should show user feedback
    // in most failure cases. This catch is a final safety net so that users
    // are not left with a silent failure if an unexpected error occurs.
    openSupportEmail({
      orderId: errorData?.orderId,
      orderData: errorData?.orderData,
      errorMessage: errorData?.errorMessage || message,
      errorCode: errorData?.errorCode,
      phoneNumber: errorData?.phoneNumber,
      address: errorData?.address,
      context: errorData?.context,
    }).catch((error) => {
      // Log error in development for debugging
      if (isDevelopment()) {
        console.error(
          '[ErrorMessageWithSupport] Unexpected error opening support email:',
          error
        );
      }
      // Show user-facing error message as final safety net
      // This should rarely be needed as openSupportEmail has comprehensive error handling
      toast.error(
        `We were unable to open your email app. Please contact ${SUPPORT_EMAIL} directly for assistance.`,
        {
          autoClose: 8000,
          position: 'top-center',
        }
      );
    });
  };
  const { supportPattern, testPattern } = useMemo(() => {
    return createSupportPattern();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!message || typeof message !== 'string') return null;

  // Check if message contains support text or email (case-insensitive)
  // Use the memoized regex pattern for consistency with the actual matching logic
  const hasSupportText = supportPattern.test(message);
  if (!hasSupportText) {
    const trimmed = message.trim();
    const sep = /[.!?]$/.test(trimmed) ? ' ' : '. ';
    const messageWithSupport = `${trimmed}${sep}${SUPPORT_TEXT}`;
    return (
      <span className={className}>
        {messageWithSupport
          .split(supportPattern)
          .filter(Boolean)
          .map((part, i) => {
            const isSupportLink = testPattern.test(part);
            if (isSupportLink) {
              return (
                <button
                  key={`support-${i}`}
                  type="button"
                  className={formStyles.supportLink}
                  onClick={handleSupportClick}
                >
                  {part}
                </button>
              );
            }
            return <span key={`text-${i}`}>{part}</span>;
          })}
      </span>
    );
  }

  /**
   * Split message and insert clickable support link
   * Handles both "Contact Support" text and support email address
   * Replaces all occurrences of support text/email with clickable links
   */
  const renderMessageWithLink = () => {
    const parts = message.split(supportPattern);

    // Filter out empty parts and use the mapped array index for key generation
    const filteredParts = parts.filter((part) => part.length > 0);
    // Edge case: If message is exactly the support text/email, split creates ['', '']
    // After filtering, we have an empty array, but we know the message contains support text
    // In this case, render a clickable support link with consistent label for better UX
    // (using SUPPORT_TEXT instead of the raw message, which might be an email address)
    if (filteredParts.length === 0) {
      return (
        <button
          type="button"
          className={formStyles.supportLink}
          onClick={handleSupportClick}
          aria-label="Contact Support via email"
        >
          {SUPPORT_TEXT}
        </button>
      );
    }

    return filteredParts.map((part, mappedIndex) => {
      // Use the mapped index to create a stable and unique key
      // Sequential indices (0, 1, 2...) are clearer than sparse original indices
      const key = `support-link-part-${mappedIndex}`;

      // Check if this part matches the support pattern (case-insensitive)
      const isSupportLink = testPattern.test(part);

      if (isSupportLink) {
        return (
          <button
            key={key}
            type="button"
            className={formStyles.supportLink}
            onClick={handleSupportClick}
            aria-label="Contact Support via email"
          >
            {part}
          </button>
        );
      }

      return <span key={key}>{part}</span>;
    });
  };

  return <span className={className}>{renderMessageWithLink()}</span>;
};

export default ErrorMessageWithSupport;
